import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Lenis from 'lenis';
import 'lenis/dist/lenis.css';
import './style.css';
import { initGlobe } from './globe.js';

gsap.registerPlugin(ScrollTrigger);

/* ── Origin globe — Three.js wireframe with EU partner pins + Carlton ── */
(() => {
  const canvas = document.getElementById('origin-globe');
  if (!canvas) return;

  let started = false;
  const start = () => {
    if (started) return;
    started = true;
    initGlobe(canvas);
  };

  // Lazy-init once the section is near the viewport — keeps idle CPU low
  // for visitors who never reach Origin. Fallback to scroll-based check
  // for environments where IntersectionObserver doesn't fire reliably.
  if (typeof IntersectionObserver !== 'undefined') {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          start();
          io.disconnect();
        }
      });
    }, { rootMargin: '300px 0px' });
    io.observe(canvas);
  }

  // Belt-and-braces fallback — kick off on first scroll past hero, or after 3s
  const onScroll = () => {
    const r = canvas.getBoundingClientRect();
    if (r.top < window.innerHeight + 600) {
      start();
      window.removeEventListener('scroll', onScroll);
    }
  };
  window.addEventListener('scroll', onScroll, { passive: true });
  setTimeout(onScroll, 0);
  setTimeout(start, 3000);
})();

/* ── Preloader — black → white → reveal ─────────────────── */
(() => {
  const pre  = document.getElementById('preload');
  const body = document.body;
  if (!pre) return;

  const start = () => {
    setTimeout(() => pre.classList.add('is-flipping'), 700);
    setTimeout(() => {
      pre.classList.add('is-done');
      body.classList.remove('is-loading');
      ScrollTrigger.refresh();
    }, 1500);
  };

  // Wait for both fonts and assets to be ready before starting the flip,
  // so the wordmark doesn't reflow mid-transition when Instrument Serif lands.
  const pageReady = (document.readyState === 'complete')
    ? Promise.resolve()
    : new Promise((resolve) => window.addEventListener('load', resolve, { once: true }));

  // 3s fallback in case fonts.ready never resolves (offline / blocked CDN).
  const fontsReady = (document.fonts && document.fonts.ready)
    ? Promise.race([
        document.fonts.ready,
        new Promise((resolve) => setTimeout(resolve, 3000)),
      ])
    : Promise.resolve();

  Promise.all([pageReady, fontsReady]).then(start);
})();

/* ── Smooth scroll (Lenis ↔ GSAP) ───────────────────────── */
const lenis = new Lenis({
  duration: 1.15,
  easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
  smoothWheel: true,
});
lenis.on('scroll', ScrollTrigger.update);
gsap.ticker.add((time) => lenis.raf(time * 1000));
gsap.ticker.lagSmoothing(0);

/* ── Mark elements that animate in on scroll ────────────── */
const revealTargets = document.querySelectorAll(
  '.section-head, .offerings-head, .material-sample, .catalogue-row, .catalogue-walloff > *, .vendor-row, .origin-map, .practice-step, .practice-guarantees, .practice-video, .checklist-item, .trade-apply, .trade-trust, .faq-item, .contact-studio, .contact-cta, .contact-trust'
);
revealTargets.forEach((el) => el.setAttribute('data-reveal', ''));

/* ── Hero entrance (no scroll needed) ───────────────────── */
const heroEls = ['.hero .eyebrow', '.hero-h', '.hero .lead', '.hero-cta', '.hero-portrait', '.colophon-col', '.about'];
gsap.set(heroEls, { opacity: 0, y: 14 });

const heroTL = gsap.timeline({ defaults: { ease: 'power3.out', duration: 0.9 } });
heroTL
  .to('.hero .eyebrow', { opacity: 1, y: 0 }, 0.15)
  .to('.hero-h',         { opacity: 1, y: 0, duration: 1.1 }, 0.30)
  .to('.hero .lead',     { opacity: 1, y: 0 }, 0.70)
  .to('.hero-cta',       { opacity: 1, y: 0 }, 0.90)
  .to('.hero-portrait',  { opacity: 1, y: 0, duration: 1.0 }, 1.10)
  .to('.colophon-col',   { opacity: 1, y: 0, stagger: 0.10 }, 1.30)
  .to('.about',          { opacity: 1, y: 0 }, 1.55);

/* ── Reveal-on-scroll for everything else ───────────────── */
/* Section heads — late trigger so cascade settles AFTER bg flip,
   not alongside it. Section.top hits 50% of viewport before reveal kicks in. */
ScrollTrigger.batch('.section-head[data-reveal], .offerings-head[data-reveal]', {
  start: 'top 50%',
  onEnter: (batch) => batch.forEach((el) => el.classList.add('is-revealed')),
  once: true,
});

/* Other reveal targets — fire earlier as content settles into view */
ScrollTrigger.batch('[data-reveal]:not(.section-head):not(.offerings-head):not(.hero *)', {
  start: 'top 88%',
  onEnter: (batch) => {
    batch.forEach((el, i) => {
      gsap.delayedCall(i * 0.05, () => el.classList.add('is-revealed'));
    });
  },
  once: true,
});

/* ── Topnav state ────────────────────────────────────────── */
const topnav = document.getElementById('topnav');
ScrollTrigger.create({
  trigger: '.hero',
  start: 'top top-=80',
  onEnter:     () => topnav.classList.add('is-scrolled'),
  onLeaveBack: () => topnav.classList.remove('is-scrolled'),
});

/* ── Active nav link as user scrolls ─────────────────────── */
const navLinks = document.querySelectorAll('.topnav-links a');
const linkById = new Map();
navLinks.forEach((a) => {
  const id = a.getAttribute('href').slice(1);
  if (id) linkById.set(id, a);
});

document.querySelectorAll('main section[id]').forEach((section) => {
  ScrollTrigger.create({
    trigger: section,
    start: 'top 50%',
    end: 'bottom 50%',
    onToggle: (self) => {
      const link = linkById.get(section.id);
      if (!link) return;
      if (self.isActive) {
        navLinks.forEach((l) => l.classList.remove('is-active'));
        link.classList.add('is-active');
      }
    },
  });
});

/* ── Progress bar ───────────────────────────────────────── */
const progressBar = document.getElementById('progress');
function updateProgress() {
  const max = document.body.scrollHeight - window.innerHeight;
  const p = max > 0 ? lenis.scroll / max : 0;
  progressBar.style.setProperty('--p', p.toFixed(4));
}
lenis.on('scroll', updateProgress);
updateProgress();

/* ── Anchor links → Lenis ───────────────────────────────── */
document.querySelectorAll('a[href^="#"]').forEach((a) => {
  a.addEventListener('click', (e) => {
    const id = a.getAttribute('href').slice(1);
    if (!id) return;
    const el = document.getElementById(id);
    if (!el) return;
    e.preventDefault();
    lenis.scrollTo(el, { offset: -80, duration: 1.4 });
  });
});

/* ── About us — expand/collapse ─────────────────────────── */
(() => {
  const wrap    = document.querySelector('.about');
  const trigger = document.querySelector('.about-trigger');
  const panel   = document.querySelector('.about-content');
  if (!wrap || !trigger || !panel) return;

  const inner = panel.querySelector('.about-inner');
  let open = false;

  trigger.addEventListener('click', () => {
    open = !open;
    wrap.dataset.open = open;
    trigger.setAttribute('aria-expanded', String(open));

    if (open) {
      panel.hidden = false;
      gsap.set(panel, { height: 'auto' });
      const targetH = panel.offsetHeight;
      gsap.fromTo(panel,
        { height: 0 },
        { height: targetH, duration: 0.65, ease: 'power3.out',
          onComplete: () => { panel.style.height = 'auto'; ScrollTrigger.refresh(); }
        }
      );
      gsap.fromTo(inner.querySelectorAll('.about-col'),
        { y: 16, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.7, stagger: 0.08, delay: 0.18, ease: 'power3.out' }
      );
    } else {
      gsap.to(panel, {
        height: 0,
        duration: 0.45,
        ease: 'power3.in',
        onComplete: () => {
          panel.hidden = true;
          panel.style.height = '';
          // No ScrollTrigger.refresh() on close — it re-evaluates triggers
          // against the shrunk layout and can mistakenly toggle is-dark.
          // Safety net: if user is still in hero, force light mode regardless.
          const hero = document.querySelector('.hero');
          if (hero && (lenis.scroll || window.scrollY) < hero.offsetHeight) {
            document.body.classList.remove('is-dark');
          }
        }
      });
    }
  });
})();

/* ── Section 01 — pinned horizontal scroll + whole-page dark ── */
(() => {
  const section = document.querySelector('.section-offerings');
  const track   = document.querySelector('.offerings-track');
  if (!section || !track) return;

  let panST, darkST;
  let resizeT;

  function setup() {
    if (panST)  { panST.kill();  panST  = null; }
    if (darkST) { darkST.kill(); darkST = null; }
    gsap.set(track, { x: 0 });

    const distance = track.scrollWidth - window.innerWidth;
    if (distance <= 0) return;

    const wrap = section.querySelector('.offerings-track-wrap');

    // Pinned only the track-wrap — the head scrolls past first naturally.
    panST = gsap.to(track, {
      x: -distance,
      ease: 'none',
      scrollTrigger: {
        trigger: wrap,
        start: 'top top',
        end: () => `+=${distance}`,
        pin: true,
        scrub: 0.6,
        invalidateOnRefresh: true,
        anticipatePin: 1,
      },
    });

    // Body dark mode — engages on offerings approach, releases when the next
    // section (Material) enters viewport. Releasing at section 02 — instead
    // of at pin end — keeps cards stable during the horizontal pan and only
    // fades back to light once vertical scroll has resumed.
    const nextSection = section.nextElementSibling;
    if (nextSection) {
      darkST = ScrollTrigger.create({
        trigger: section,
        start: 'top 65%',
        endTrigger: nextSection,
        end: 'top top',
        onToggle: (self) => document.body.classList.toggle('is-dark', self.isActive),
      });
    }
  }

  setup();
  window.addEventListener('load', () => setTimeout(setup, 100));
  window.addEventListener('resize', () => {
    clearTimeout(resizeT);
    resizeT = setTimeout(setup, 200);
  });
})();

/* ── Body dark mode through Origin → Practice → Letters ───── */
(() => {
  const start = document.querySelector('#origin');
  const end = document.querySelector('#trade');
  if (!start || !end) return;
  ScrollTrigger.create({
    trigger: start,
    start: 'top 65%',
    endTrigger: end,
    end: 'top top',
    onToggle: (self) => document.body.classList.toggle('is-dark', self.isActive),
  });
})();

/* ── Practice video — expand on scroll ────────────────────── */
(() => {
  const video = document.querySelector('.practice-video');
  if (!video) return;
  gsap.fromTo(video,
    { scale: 0.78 },
    {
      scale: 1,
      ease: 'none',
      scrollTrigger: {
        trigger: video,
        start: 'top bottom',
        end: 'top center',
        scrub: 0.8,
      },
    }
  );
})();

/* ── Megafoot wordmark — scale-up on scroll into view ─────── */
(() => {
  const mark = document.querySelector('.mark-text');
  if (!mark) return;
  gsap.fromTo(mark,
    { scale: 0.94, opacity: 0.55 },
    {
      scale: 1,
      opacity: 1,
      ease: 'none',
      scrollTrigger: {
        trigger: mark,
        start: 'top bottom',
        end: 'bottom bottom',
        scrub: 0.6,
      },
    }
  );
})();

/* ── Sign-in — modal with sign-in / forgot / request-access views ── */
(() => {
  const modal = document.getElementById('signin-modal');
  if (!modal) return;
  const card  = modal.querySelector('.signin-card');
  let lastFocus = null;

  const setView = (name) => {
    if (!['signin', 'forgot', 'access'].includes(name)) name = 'signin';
    card.dataset.view = name;
    setTimeout(() => {
      const first = card.querySelector(`.signin-view[data-view-name="${name}"] input`);
      if (first) first.focus();
    }, 60);
  };

  const open = (view) => {
    lastFocus = document.activeElement;
    modal.removeAttribute('hidden');
    document.body.classList.add('is-signin-open');
    if (typeof lenis !== 'undefined') lenis.stop();
    setView(view || 'signin');
  };

  const close = () => {
    document.body.classList.remove('is-signin-open');
    setTimeout(() => {
      modal.setAttribute('hidden', '');
      setView('signin');
    }, 280);
    if (typeof lenis !== 'undefined') lenis.start();
    if (lastFocus && typeof lastFocus.focus === 'function') {
      try { lastFocus.focus(); } catch {}
    }
  };

  // Close mobile menu (and unlock scroll) when modal opens from inside it
  const closeMobileMenu = () => {
    if (!document.body.classList.contains('is-menu-open')) return;
    document.body.classList.remove('is-menu-open');
    const menu = document.getElementById('mobile-menu');
    const burger = document.getElementById('topnav-burger');
    if (menu) menu.setAttribute('hidden', '');
    if (burger) burger.setAttribute('aria-expanded', 'false');
  };

  // Pre-fill the access-view email field when called with a value
  const prefillAccessEmail = (value) => {
    if (!value) return;
    const input = card.querySelector('.signin-view[data-view-name="access"] input[name="email"]');
    if (input) input.value = value;
  };

  // Hijack #signin and #access links — open the modal from any position
  document.addEventListener('click', (e) => {
    const signinLink = e.target.closest('a[href="#signin"]');
    if (signinLink) {
      e.preventDefault();
      closeMobileMenu();
      open('signin');
      return;
    }

    const accessLink = e.target.closest('a[href="#access"]');
    if (accessLink) {
      e.preventDefault();
      closeMobileMenu();
      open('access');
      return;
    }

    // Inside the modal — view switch links
    const viewLink = e.target.closest('[data-view-target]');
    if (viewLink && card.contains(viewLink)) {
      e.preventDefault();
      setView(viewLink.dataset.viewTarget);
      return;
    }

    // Close handlers — backdrop, X
    if (e.target.closest('[data-signin-close]')) {
      close();
    }
  });

  // Trade-apply form (section 07) → open modal in access view, carrying email
  const tradeApplyForm = document.querySelector('.trade-apply');
  if (tradeApplyForm) {
    tradeApplyForm.addEventListener('submit', (e) => {
      e.preventDefault();
      const emailInput = tradeApplyForm.querySelector('input[name="email"]');
      const value = emailInput ? emailInput.value.trim() : '';
      prefillAccessEmail(value);
      open('access');
    });
  }

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && document.body.classList.contains('is-signin-open')) {
      close();
    }
  });

  // Show/Hide password (only in sign-in view)
  const pwToggle = modal.querySelector('[data-signin-toggle]');
  const pwInput  = modal.querySelector('input[name="password"]');
  if (pwToggle && pwInput) {
    pwToggle.addEventListener('click', () => {
      const showing = pwInput.type === 'text';
      pwInput.type = showing ? 'password' : 'text';
      pwToggle.textContent = showing ? 'Show' : 'Hide';
      pwToggle.setAttribute('aria-label', showing ? 'Show password' : 'Hide password');
    });
  }

  // Form submission — placeholder until WP backend is wired
  const formMessages = {
    signin: 'Backend pending — connect at deploy',
    forgot: 'If the address is on file, a reset link is on its way',
    access: 'Application received — verification ~1 business day',
  };

  modal.querySelectorAll('form').forEach((form) => {
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const status = form.querySelector('.signin-status');
      const which = form.dataset.form;
      if (status) {
        status.hidden = false;
        status.textContent = formMessages[which] || 'Submitted';
        setTimeout(() => { status.hidden = true; status.textContent = ''; }, 4000);
      }
    });
  });
})();

/* ── Mobile menu — hamburger toggle ──────────────────────── */
(() => {
  const burger = document.getElementById('topnav-burger');
  const menu   = document.getElementById('mobile-menu');
  if (!burger || !menu) return;

  const close = () => {
    document.body.classList.remove('is-menu-open');
    burger.setAttribute('aria-expanded', 'false');
    menu.setAttribute('hidden', '');
    if (typeof lenis !== 'undefined') lenis.start();
  };
  const open = () => {
    document.body.classList.add('is-menu-open');
    burger.setAttribute('aria-expanded', 'true');
    menu.removeAttribute('hidden');
    if (typeof lenis !== 'undefined') lenis.stop();
  };

  burger.addEventListener('click', () => {
    if (document.body.classList.contains('is-menu-open')) close();
    else open();
  });

  menu.querySelectorAll('a').forEach((a) => {
    a.addEventListener('click', () => setTimeout(close, 150));
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && document.body.classList.contains('is-menu-open')) close();
  });
})();

/* ── Custom cursor — dot + lerp ring ─────────────────────── */
(() => {
  if (!matchMedia('(hover: hover) and (pointer: fine)').matches) return;

  const cursor = document.getElementById('cursor');
  if (!cursor) return;
  const dot   = cursor.querySelector('.cursor-dot');
  const ring  = cursor.querySelector('.cursor-ring');
  const arrow = cursor.querySelector('.cursor-arrow');
  if (!dot || !ring) return;

  // CTA buttons get a directional arrow cursor — primary actions feel different from inline links
  const ctaSelector =
    '.btn-primary, .btn-secondary, .apply-submit, .signin-submit, .play-button, .mobile-menu-primary';

  const interactiveSelector =
    'a, button, summary, input, textarea, [role="button"], [tabindex], ' +
    '.practice-video-frame, .sample-chip, .hero-portrait, .map-frame, .letter-card';

  // Surfaces painted dark regardless of body class — flips cursor to cream
  const darkSurfaces = '.topnav, .btn-primary, .apply-submit, .play-button, .signin-submit, .signin-card .signin-submit, .mobile-menu-primary';

  let tx = -100, ty = -100;   // target (raw pointer)
  let rx = -100, ry = -100;   // ring (lerped)
  let started = false;

  const tick = () => {
    rx += (tx - rx) * 0.18;
    ry += (ty - ry) * 0.18;
    dot.style.transform  = `translate3d(${tx}px, ${ty}px, 0) translate(-50%, -50%)`;
    ring.style.transform = `translate3d(${rx}px, ${ry}px, 0) translate(-50%, -50%)`;
    if (arrow) arrow.style.translate = `${tx}px ${ty}px`;
    requestAnimationFrame(tick);
  };
  tick();

  const updateState = (target) => {
    if (!target || !target.closest) {
      cursor.classList.remove('is-interactive', 'is-on-dark', 'is-cta');
      return;
    }
    const isCta = !!target.closest(ctaSelector);
    cursor.classList.toggle('is-on-dark', !!target.closest(darkSurfaces));
    cursor.classList.toggle('is-cta', isCta);
    // Don't double-up: when over CTA, .is-interactive logic still runs but ring is hidden via CSS
    cursor.classList.toggle('is-interactive', !isCta && !!target.closest(interactiveSelector));
  };

  document.addEventListener('mousemove', (e) => {
    tx = e.clientX;
    ty = e.clientY;
    if (!started) {
      started = true;
      // First paint — snap ring to dot so it doesn't glide in from (-100,-100)
      rx = tx; ry = ty;
      cursor.classList.add('is-ready');
    }
  });
  document.addEventListener('mouseover', (e) => updateState(e.target));
  document.addEventListener('mousedown', () => cursor.classList.add('is-clicking'));
  document.addEventListener('mouseup', () => cursor.classList.remove('is-clicking'));
  document.addEventListener('mouseleave', () => cursor.classList.remove('is-ready'));
  document.addEventListener('mouseenter', () => started && cursor.classList.add('is-ready'));
})();

/* ── Section 06 — Letters: pinned horizontal scroll LEFT ──── */
(() => {
  const section = document.querySelector('.section-letters-carousel');
  const track   = document.querySelector('.letters-track');
  if (!section || !track) return;

  let panST, resizeT;

  function setup() {
    if (panST) { panST.kill(); panST = null; }
    gsap.set(track, { x: 0 });

    const distance = track.scrollWidth - window.innerWidth;
    if (distance <= 0) return;

    const wrap = section.querySelector('.letters-track-wrap');

    // Mirror of Offerings: track starts shifted left (negative x), animates
    // to x: 0 — content visually flows rightward as user scrolls down.
    panST = gsap.fromTo(track,
      { x: -distance },
      {
        x: 0,
        ease: 'none',
        scrollTrigger: {
          trigger: wrap,
          start: 'top top',
          end: () => `+=${distance}`,
          pin: true,
          scrub: 0.6,
          invalidateOnRefresh: true,
          anticipatePin: 1,
        },
      }
    );
  }

  setup();
  window.addEventListener('load', () => setTimeout(setup, 100));
  window.addEventListener('resize', () => {
    clearTimeout(resizeT);
    resizeT = setTimeout(setup, 200);
  });
})();

/* ── Hero cursor-image trail ────────────────────────────── */
(() => {
  if (matchMedia('(hover: none)').matches) return;

  const hero  = document.querySelector('.hero');
  const stage = document.querySelector('.hero-cursor-stage');
  if (!hero || !stage) return;

  // Six placeholder product tiles cycling under cursor.
  // Tones stay in archive/photographic neutrals (no metallic gold).
  const tiles = [
    { label: 'PORCELAIN · 01', tone: '#EAD9B5', shape: 'oval' },
    { label: 'FRAME · 02',     tone: '#C9B89A', shape: 'frame' },
    { label: 'BRONZE · 03',    tone: '#B5A38A', shape: 'rect' },
    { label: 'CRYSTAL · 04',   tone: '#D7DCDF', shape: 'rect' },
    { label: 'CROSS · 05',     tone: '#CFD2D2', shape: 'cross' },
    { label: 'PORCELAIN · 06', tone: '#DBD3C2', shape: 'oval' },
  ];

  const buildSvg = ({ label, tone, shape }) => {
    let inner = '';
    if (shape === 'oval') {
      inner = `<ellipse cx='160' cy='180' rx='72' ry='98' fill='rgba(15,15,15,0.18)'/>`;
    } else if (shape === 'rect') {
      inner = `<rect x='84' y='80' width='152' height='200' fill='rgba(15,15,15,0.16)'/>`;
    } else if (shape === 'frame') {
      inner = `<rect x='70' y='66' width='180' height='228' fill='none' stroke='rgba(15,15,15,0.32)' stroke-width='6'/><ellipse cx='160' cy='180' rx='62' ry='82' fill='rgba(15,15,15,0.14)'/>`;
    } else if (shape === 'cross') {
      inner = `<path d='M152 70 L168 70 L168 170 L240 170 L240 186 L168 186 L168 300 L152 300 L152 186 L80 186 L80 170 L152 170 Z' fill='rgba(15,15,15,0.20)'/>`;
    }
    const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 320 410' preserveAspectRatio='xMidYMid slice'>` +
      `<rect width='320' height='410' fill='${tone}'/>` +
      inner +
      `<text x='18' y='390' font-family='JetBrains Mono, monospace' font-size='10.5' fill='rgba(15,15,15,0.55)' letter-spacing='2.4'>${label}</text>` +
      `</svg>`;
    return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
  };

  const dataUrls = tiles.map(buildSvg);

  let lastX = -9999, lastY = -9999, idx = 0;
  const minDist = 240;
  const maxOnScreen = 6;
  let maxY = Infinity;

  // Trail covers the entire hero except where the portrait and the 2-column
  // colophon live. Boundary sits right above hero-foot's top hairline.
  const heroFoot = hero.querySelector('.hero-foot');
  function updateStageBound() {
    if (!heroFoot) return;
    const heroRect = hero.getBoundingClientRect();
    const footRect = heroFoot.getBoundingClientRect();
    const buffer = 12;
    const ceil = (footRect.top - heroRect.top) - buffer;
    stage.style.setProperty('--stage-bottom', `${heroRect.height - ceil}px`);
    maxY = ceil;
  }
  updateStageBound();
  requestAnimationFrame(updateStageBound);
  window.addEventListener('resize', updateStageBound);
  window.addEventListener('load', updateStageBound);
  if ('ResizeObserver' in window) {
    new ResizeObserver(updateStageBound).observe(hero);
  }

  hero.addEventListener('mousemove', (e) => {
    const rect = hero.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    if (y > maxY) return;
    if (Math.hypot(x - lastX, y - lastY) < minDist) return;
    lastX = x; lastY = y;
    spawn(x, y);
  });

  hero.addEventListener('mouseleave', () => {
    lastX = -9999; lastY = -9999;
  });

  function spawn(x, y) {
    while (stage.children.length >= maxOnScreen) {
      stage.firstElementChild.remove();
    }
    const el = document.createElement('div');
    el.className = 'cursor-img';
    el.style.left = `${x}px`;
    el.style.top  = `${y}px`;
    el.style.backgroundImage = `url("${dataUrls[idx % dataUrls.length]}")`;
    stage.appendChild(el);
    idx++;

    const rot = (Math.random() - 0.5) * 7;
    gsap.timeline()
      .fromTo(el,
        { opacity: 0, scale: 0.9, rotation: rot },
        { opacity: 1, scale: 1, rotation: rot, duration: 0.32, ease: 'power2.out' }
      )
      .to(el, {
        opacity: 0,
        scale: 1.04,
        rotation: rot,
        duration: 1.1,
        delay: 0.6,
        ease: 'power2.in',
        onComplete: () => el.remove(),
      });
  }
})();
